import React, { useCallback, useMemo } from 'react';
import WorkOffRoundedIcon from '@mui/icons-material/WorkOffRounded';
import Stack from '@mui/material/Stack';
import { Typography, Tooltip, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FinKitAvatar } from '../finKitAvatar';

interface MemberInfoCellProps {
  hideAvatar?: boolean;
  disabled?: boolean;

  link?: string;

  userInfo: {
    avatarUrl?: string;
    fullName: string;
    stream?: string;
    country?: string;
    city?: string;
  };
  roleLabel?: string;

  avatarSmall?: boolean;
  avatarMedium?: boolean;
  avatarLarge?: boolean;

  length?: 'full' | 'full-56px';
}

export function MemberInfoCell({
  hideAvatar,
  disabled,

  link,

  userInfo,
  roleLabel,

  avatarSmall,
  avatarMedium = true,
  avatarLarge,

  length = 'full-56px',
}: MemberInfoCellProps): JSX.Element {
  const navigate = useNavigate();

  const onLinkClick = useCallback(() => {
    if (link) {
      navigate(link);
    }
  }, [navigate, link]);

  return link ? (
    <Link
      sx={{
        whiteSpace: 'nowrap',
        textAlign: 'left',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
      }}
      component="button"
      variant="body1"
      onClick={onLinkClick}
    >
      <MemberInfoCellContent
        userInfo={userInfo}
        roleLabel={roleLabel}
        hideAvatar={hideAvatar}
        avatarSmall={avatarSmall}
        avatarMedium={avatarMedium}
        avatarLarge={avatarLarge}
        length={length}
        disabled={disabled}
      />
    </Link>
  ) : (
    <MemberInfoCellContent
      userInfo={userInfo}
      hideAvatar={hideAvatar}
      roleLabel={roleLabel}
      avatarSmall={avatarSmall}
      avatarMedium={avatarMedium}
      avatarLarge={avatarLarge}
      length={length}
      disabled={disabled}
    />
  );
}

const MemberInfoCellContent = ({
  userInfo,
  roleLabel,
  hideAvatar,
  avatarSmall,
  avatarMedium,
  avatarLarge,
  length,
  disabled,
}: MemberInfoCellProps) => {
  const textStyles = useMemo(
    () => (disabled ? { opacity: 0.7 } : {}),
    [disabled],
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        maxWidth: '100%',
        width: '100%',
      }}
    >
      {!hideAvatar && (
        <FinKitAvatar
          small={avatarSmall}
          medium={avatarMedium}
          large={avatarLarge}
          fullName={userInfo.fullName}
          imageUrl={userInfo.avatarUrl}
          country={userInfo.country}
          city={userInfo.city}
        />
      )}
      <Stack
        ml={2}
        sx={{
          width: length === 'full-56px' ? 'calc(100% - 56px)' : '100%',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {userInfo.fullName ? (
            <Typography
              variant="body1"
              sx={{
                marginRight: 1,
                ...textStyles,
                width: disabled ? 'calc(100% - 30px)' : '100%',
              }}
            >
              {userInfo.fullName}
            </Typography>
          ) : (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                marginRight: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...textStyles,
              }}
            >
              {'(name is missing)'}
            </Typography>
          )}
          {disabled && (
            <Tooltip title="Deactivated" enterDelay={300} placement="top">
              <WorkOffRoundedIcon color="disabled" sx={{ fontSize: '16px' }} />
            </Tooltip>
          )}
        </Stack>
        <Typography
          variant="body2"
          sx={{
            width: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...textStyles,
          }}
        >
          {roleLabel ? `${roleLabel} / ` : ''}
          {userInfo.stream}
        </Typography>
      </Stack>
    </Stack>
  );
};
